import { Box, Typography, Button } from "@mui/material";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { updateSEO } from "../utils/SEO";

// Constants
const ABOUT_TEXT = {
  title: "Nicole Hahn",
  subtitle: "Owner/Principal Designer",
  bio: [
    "As founder and principal designer, Nicole has been interested in art and design from a very young age. She holds an Associate Degree in Interior Design from FIDM and a Bachelor of Arts Degree in Interior Design from Bellevue College's CIDA-accredited design program. Prior to launching NHD, Nicole worked for several design companies that have provided her with experience in everything from custom furnishings to designing multi-million dollar custom homes.",
    "Nicole has a passion for creating one-of-a-kind spaces that encompass an earthy, natural, and timeless design style. It is Nicole's attention to detail and a love for design that allows her to create interiors that her clients love.",
    "When Nicole is not designing, she enjoys traveling and spending time with her husband and two young daughters."
  ],
  credo: {
    title: "My Credo",
    content: [
      "The oldest thing in your house should not be you. The use of antiques and reclaimed materials tell a story, add character, and make a space feel lived in.",
      "A well designed space is functional, calming, and a direct reflection of the people who occupy it.",
      "The use of natural materials and earthy tones create environments that feel authentic and welcoming.",
      "-N. Hahn"
    ]
  }
};

// Extracted styles for better maintainability
const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
  },
  mainSection: {
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    alignItems: { xs: "center", md: "flex-start" },
    justifyContent: "center",
    gap: { xs: 3, md: 4 },
    padding: { xs: "20px", sm: "30px", md: "40px" },
    paddingBottom: { xs: "1rem", sm: "1rem", md: "1rem" },
    backgroundColor: "#F5F5F5",
    width: "100%",
    maxWidth: "1400px",
    margin: "0 auto",
  },
  profileImage: {
    width: { xs: "90%", sm: "60%", md: "30%" },
    maxWidth: { xs: "300px", md: "none" },
    height: "auto",
    objectFit: "cover",
    borderRadius: "8px",
    border: "6px solid #FFFFFF",
    boxShadow: "0 12px 28px rgba(0, 0, 0, 0.18)",
    marginBottom: { xs: "1.5rem", md: 0 },
  },
  aboutText: {
    width: { xs: "100%", sm: "80%", md: "50%" },
    textAlign: { xs: "left", md: "left" },
    padding: { xs: "0 10px", sm: "0 15px", md: 0 },
    display: "flex",
    flexDirection: "column",
    justifyContent: { xs: "center", sm: "center", md: "flex-start" },
    marginTop: { xs: 0, md: 0 },
    alignSelf: { xs: "center", md: "flex-start" }
  },
  titleDivider: {
    width: "60px",
    height: "3px",
    backgroundColor: "#464d22",
    border: "none",
    margin: 0,
    marginBottom: "1.5rem",
  },
  bioText: {
    fontSize: { xs: "1rem", sm: "1.2rem", md: "1.5rem" },
    lineHeight: { xs: 1.6, md: 1.6 },
    letterSpacing: "0.01em",
    marginBottom: "2rem",
    maxWidth: "65ch",
    fontFamily: '"Old Standard TT", serif',
  },
  credoSection: {
    width: "100%",
    backgroundColor: "#e6e4df",
    color: "black",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
    display: "flex",
    justifyContent: "center",
  },
  credoContainer: {
    width: { xs: "90%", sm: "85%", md: "60%" },
    textAlign: "center",
    padding: { xs: "20px", sm: "30px", md: "40px" },
    maxWidth: "none",
  },
  credoText: {
    fontSize: { xs: "1.2rem", sm: "1.8rem", md: "2.2rem" },
    fontFamily: '"Caveat", cursive',
    fontWeight: { xs: 300, sm: 300, md: 400 },
    paddingBottom: { xs: "1.5rem", sm: "2rem", md: "4rem" },
    paddingTop: { xs: "1.5rem", sm: "2rem", md: "4rem" },
    lineHeight: { xs: 1.6, md: 1.8 },
    "& > strong": {
      display: "block",
      marginBottom: "1rem",
      fontSize: { xs: "2rem", sm: "2.6rem", md: "3rem" },
      color: "#000000",
      fontFamily: '"Caveat", cursive',
      fontWeight: 500
    }
  },
};

/**
 * About component that displays Nicole's bio and design philosophy
 * @returns {JSX.Element} The rendered About component
 */
const About = () => {
  useEffect(() => {
    updateSEO({
      title: "About Nicole Hahn | Principal Interior Designer | Seattle, WA",
      description: "Meet Nicole Hahn, owner and principal designer at Nicole Hahn Designs. With degrees from FIDM and Bellevue College's CIDA-accredited program, Nicole specializes in creating timeless, earthy, and natural design spaces in Seattle, Bellevue, and Tacoma.",
      url: "https://nicolehahndesigns.com/about",
      image: "https://nicolehahndesigns.com/portrait.jpg"
    });

    // Add enhanced structured data for About page
    const aboutScript = document.createElement('script');
    aboutScript.type = 'application/ld+json';
    aboutScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "About Nicole Hahn | Interior Designer",
      "description": "Learn about Nicole Hahn, owner and principal designer at Nicole Hahn Designs. Nicole has an Associate Degree from FIDM and a Bachelor of Arts in Interior Design from Bellevue College's CIDA-accredited program.",
      "url": "https://nicolehahndesigns.com/about",
      "mainEntity": {
        "@type": "Person",
        "name": "Nicole Hahn",
        "jobTitle": "Owner/Principal Designer",
        "worksFor": {
          "@type": "HomeAndConstructionBusiness",
          "name": "Nicole Hahn Designs",
          "url": "https://nicolehahndesigns.com"
        },
        "alumniOf": [
          {
            "@type": "EducationalOrganization",
            "name": "FIDM",
            "description": "Fashion Institute of Design & Merchandising"
          },
          {
            "@type": "EducationalOrganization",
            "name": "Bellevue College",
            "description": "CIDA-accredited Interior Design program"
          }
        ],
        "description": "Interior designer specializing in custom new construction, remodels, and kitchen and bath design with an earthy, natural, and timeless design style. Nicole has experience in custom furnishings and multi-million dollar custom homes.",
        "knowsAbout": [
          "Interior Design",
          "Custom Home Design",
          "Kitchen Design",
          "Bathroom Design",
          "Home Remodeling",
          "Space Planning",
          "Custom Furnishings"
        ],
        "award": "CIDA-accredited design program graduate"
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://nicolehahndesigns.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "About",
            "item": "https://nicolehahndesigns.com/about"
          }
        ]
      }
    });
    document.head.appendChild(aboutScript);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.text.includes('"@type": "AboutPage"')) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <Box
      component="main"
      role="main"
      aria-label="About Nicole Hahn"
      sx={styles.container}
    >
      <Box sx={styles.mainSection}>
        {/* Profile Image */}
        <Box
          component="img"
          src="/portrait.jpg"
          alt="Nicole Hahn, Owner and Principal Interior Designer at Nicole Hahn Designs, specializing in luxury custom homes and remodels in Seattle, Washington"
          loading="eager"
          fetchpriority="high"
          sx={styles.profileImage}
        />

        {/* About Text */}
        <Box sx={styles.aboutText}>
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontSize: { xs: "2rem", sm: "2.5rem", md: "2.5rem" },
              color: "#000000",
              fontFamily: '"Bebas Neue", sans-serif',
              fontWeight: { xs: 300, sm: 300, md: 400 },
              marginBottom: "0.25rem",
              lineHeight: 1.1,
            }}
          >
            {ABOUT_TEXT.title}
          </Typography>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: "1.5rem", sm: "1.8rem", md: "2rem" },
              color: "#000000",
              fontFamily: '"Bebas Neue", sans-serif',
              fontWeight: { xs: 300, sm: 300, md: 400 },
              lineHeight: 1.1,
              marginBottom: "1rem",
            }}
          >
            {ABOUT_TEXT.subtitle}
          </Typography>
          <Box component="hr" sx={styles.titleDivider} />
          <Typography
            variant="body1"
            component="div"
            sx={styles.bioText}
          >
            {ABOUT_TEXT.bio.map((paragraph, index) => (
              <Typography
                key={index}
                component="p"
                sx={{
                  marginBottom: "1.5rem",
                  fontSize: "inherit",
                  lineHeight: "inherit",
                  fontFamily: '"Old Standard TT", serif'
                }}
              >
                {paragraph}
              </Typography>
            ))}
          </Typography>
        </Box>
      </Box>

      {/* Credo Section */}
      <Box
        component="section"
        aria-label="Design Philosophy"
        sx={styles.credoSection}
      >
        <Box sx={styles.credoContainer}>
          <Typography
            variant="body1"
            component="div"
            sx={styles.credoText}
          >
            <strong>{ABOUT_TEXT.credo.title}</strong>
            {ABOUT_TEXT.credo.content.map((paragraph, index) => (
              <Typography
                key={index}
                component="p"
                sx={{
                  marginBottom: "1rem",
                  fontSize: "inherit",
                  lineHeight: "inherit",
                  fontFamily: '"Caveat", cursive',
                  fontWeight: 500
                }}
              >
                {paragraph}
              </Typography>
            ))}
          </Typography>
        </Box>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: { xs: '2rem 2rem 4rem', sm: '2rem 2rem 4rem', md: '2rem 2rem 4rem' },
          gap: '1.5rem',
          backgroundColor: "#F5F5F5",
          marginTop: { xs: '1rem', sm: '1rem', md: '1rem' }
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
            color: 'black',
            maxWidth: '800px',
            lineHeight: 1.5,
            textAlign: 'center',
            fontFamily: '"Old Standard TT", serif'
          }}
        >
          To work with Nicole on your upcomming project, please inquire
        </Typography>
        <Button
          variant="contained"
          component={Link}
          to="/contact"
          sx={{
            padding: '1rem 2rem',
            backgroundColor: '#464d22',
            color: 'white',
            fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem' },
            fontWeight: 500,
            letterSpacing: '0.02em',
            borderRadius: '20px',
            fontFamily: '"Bebas Neue", sans-serif',
            minWidth: { xs: "200px", sm: "250px", md: "300px" },
            '&:hover': {
              backgroundColor: '#757a5c',
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            },
            transition: 'all 0.3s ease-in-out',
          }}
        >
          Inquire
        </Button>
      </Box>
    </Box>
  );
};

export default About;